import React from "react";
import { useMemo } from "react";

import { useForm } from "react-hook-form";
import { UseQueryResult } from "react-query";

import Modal from './../Shared/Modal';
import { Alert } from "../Shared/Alert";
import LoadingWheel from "../Shared/LoadingWheel";
import { ButtonInput } from "../Shared/Inputs/ButtonInput";
import useAgencyBranches from "../../data/useAgencyBranches";
import useBranchDepartments from "../../data/useBranchDepartments";
import useBranchMappings from "../../data/useBranchMappings";
import { IIntegration } from "../../models/integration/Integration";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IDefaultResponseWithData } from "../../models/system/DefaultResponse";
import { StyledSelectInput } from "../Shared/StyledInputs/StyledSelectInput";
import clsx from "clsx";
import ActionButton from "../Shared/ActionButton";
import { TrashIcon } from "@heroicons/react/outline";
import { ICRMBranch } from "../../models/integration/CRMBranch";
import { IBranchMapping } from './../../models/integration/BranchMapping';

interface BranchMappingFormFields {
  branchId: string;
  externalBranchId: string;
  departmentId?: string;
}

interface AltoBranchMappingModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  integration: IIntegration;
  useCrmBranches: (agencyId: string) => { branches: UseQueryResult<IDefaultResponseWithData<ICRMBranch[]>> };
}

export default function BranchMappingModal({ open, setOpen, integration, useCrmBranches }: AltoBranchMappingModalProps) {
  const { register, setValue, watch, handleSubmit, reset } = useForm<BranchMappingFormFields>();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Branch Mapping");
  const { branches: crmBranches } = useCrmBranches(integration.agencyId);
  const { branches } = useAgencyBranches(integration.agencyId);
  const { branchMappings, add, remove } = useBranchMappings(integration.id, integration.agencyId);

  const selectedBranchId = watch("branchId");
  const selectedDepartmentId = watch("departmentId");
  const { departments } = useBranchDepartments(selectedBranchId);

  const departmentOptions = useMemo(() => {
    const base = [{ label: "— Any department —", value: "" }];
    if (!selectedBranchId || departments.isLoading || !departments.data) return base;
    return base.concat(departments.data.map((d: { id: string; name: string }) => ({ label: d.name, value: d.id })));
  }, [selectedBranchId, departments.data, departments.isLoading]);

  const trulyUnmappedBranches = useMemo(() => {
    if (crmBranches.isLoading || !crmBranches.data?.data || branchMappings.isLoading || !branchMappings.data) return [];
    return crmBranches.data.data.filter(b =>
      !branchMappings.data.some((m: IBranchMapping) => m.externalBranchId === b.id.toString())
    );
  }, [crmBranches.data, crmBranches.isLoading, branchMappings.data, branchMappings.isLoading]);

  const availableExternalBranches = useMemo(() => {
    if (crmBranches.isLoading || !crmBranches.data?.data || branchMappings.isLoading || !branchMappings.data) return [];
    return crmBranches.data.data.filter(b => {
      const existing = branchMappings.data.filter((m: IBranchMapping) => m.externalBranchId === b.id.toString());
      if (!selectedDepartmentId) {
        return !existing.some((m: IBranchMapping) => m.departmentId == null);
      }
      return !existing.some((m: IBranchMapping) => m.departmentId === selectedDepartmentId);
    });
  }, [crmBranches.data, crmBranches.isLoading, branchMappings.data, branchMappings.isLoading, selectedDepartmentId]);

  const onSubmit = (data: BranchMappingFormFields) => {
    if (!data.branchId || !data.externalBranchId) return;

    const externalBranch = crmBranches.data?.data.find(b => b.id.toString() === data.externalBranchId);

    if (!externalBranch) return;

    let body = {
      branchId: data.branchId,
      externalBranchId: data.externalBranchId,
      externalBranchName: externalBranch.name,
      departmentId: data.departmentId ? data.departmentId : null,
    };

    // @ts-ignore
    add.mutate(body, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        reset();
      }
    });
  };

  const isError = branchMappings.isError || crmBranches.isError || branches.isError;
  const isLoading = branchMappings.isLoading || crmBranches.isLoading || branches.isLoading;

  return (
    <Modal open={open} setOpen={setOpen} width={"max-w-2xl"}>
      <h1 className="text-lg font-semibold">{integration.appName} Branch Mappings</h1>
      <>
        {
          isError ?
            (
              <div className="my-4">
                <Alert type="error" title="Error" message="There was an error loading the branch mappings. Please try again later." />
              </div>
            )
            :
            isLoading ?
              (
                <div className="flex items-center justify-center py-10">
                  <LoadingWheel width="w-8" height="h-8" />
                </div>
              )
              : (
                <>
                  {trulyUnmappedBranches.length > 0 && (
                    <div className="my-2">
                      <Alert type="warning" title="Unmapped Branches" message={`There are ${trulyUnmappedBranches.length} unmapped branches.`} />
                    </div>)}
                  <table className="my-4 w-full border border-gray-300">
                    <thead>
                      <tr className="bg-gray-200 divide-x divide-gray-300">
                        <th className="px-3 py-2">Estatetrack Branch</th>
                        <th className="px-3 py-2">Department</th>
                        <th className="px-3 py-2">{integration.appName} Branch</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {branchMappings.data.length === 0 ? (
                        <tr>
                          <td className="px-3 text-sm text-gray-600 italic text-center py-4" colSpan={4}>No Branch Mappings</td>
                        </tr>
                      ) : (
                        branchMappings.data.map((mapping: IBranchMapping, i: number) => (
                          <tr key={mapping.id} className={clsx("divide-x divide-gray-300", i % 2 === 0 ? "bg-white" : "bg-gray-50")}>
                            <td width="35%" className="px-3 py-2">{mapping.branch.name}</td>
                            <td width="22%" className="px-3 py-2">{mapping.department?.name ?? "—"}</td>
                            <td width="35%" className="px-3 py-2">
                              {mapping.externalBranchName}
                            </td>
                            <td width="8%" className="text-center">
                              <ActionButton
                                tooltip="Delete Mapping"
                                // @ts-ignore
                                onClick={() => remove.mutate(mapping.id, deleteHandlers)}
                                Icon={TrashIcon}
                              />
                            </td>
                          </tr>
                        )))}
                    </tbody>
                  </table>

                  <form onSubmit={handleSubmit(onSubmit)} className="mt-4 pt-2 border-t space-y-4">
                    <h1 className="font-semibold">New Mapping</h1>

                    <StyledSelectInput
                      label="Estatetrack Branch"
                      registerName="branchId"
                      register={register}
                      setValue={setValue}
                      watch={watch}
                      options={branches.data.map((b: any) => ({ label: b.name, value: b.id }))}
                      onChange={() => setValue("departmentId", "")}
                    />
                    {selectedBranchId && (
                      <StyledSelectInput
                        label="Department (optional)"
                        registerName="departmentId"
                        register={register}
                        setValue={setValue}
                        watch={watch}
                        options={departmentOptions}
                      />
                    )}
                    <StyledSelectInput
                      label={`${integration.appName} Branch`}
                      registerName="externalBranchId"
                      register={register}
                      setValue={setValue}
                      watch={watch}
                      options={availableExternalBranches.map((b: ICRMBranch) => ({ label: b.name, value: b.id }))}
                    />
                    <ButtonInput label="Save" isSubmit onClick={() => { }} classes="" />
                  </form>
                </>
              )
        }
      </>
    </Modal>
  )
}